import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import { breakpointSizes, mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import OutlineTextButton from 'LuxKit/Button/OutlineTextButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalContext from 'contexts/ModalContext'
import BusinessTravellerRequestDemoModal from 'businessTraveller/components/demo-form/BusinessTravellerRequestDemoModal'
import { getImageUrl } from 'lib/image/imageUtils'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { BUSINESS_TRAVELLER_BUSINESS_SIGNUP_ROUTE } from 'components/App/Routes/routeConstants'

const SectionBackground = styled.div`
  background: rgba(243, 243, 244, 0.50);
`

const SectionContainer = styled(LayoutContainer)`
  padding-top: ${rem(56)};
  padding-bottom: ${rem(56)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(70)};
    padding-bottom: ${rem(70)};
  }

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(80)};
    padding-bottom: ${rem(80)};
  }
`

const FeaturesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${rem(28)};
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: ${rem(24)}
  }
`

const FeatureCard = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${rem(16)};
  align-items: flex-start;

  ${mediaQueryUp.tablet} {
    flex-direction: column;
    gap: ${rem(12)};
  }
`

const FeatureImage = styled.img`
  width: ${rem(100)};
  height: ${rem(100)};
  border-radius: ${rem(8)};
  object-fit: cover;
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    width: ${rem(267)};
    height: ${rem(330)};
    aspect-ratio: 1;
  }
`

const FeatureTextGroup = styled(Group)`
  padding-top: ${rem(8)};

  ${mediaQueryUp.tablet} {
    padding-top: 0;
    max-width: ${rem(267)};
  }
`

const SectionHeading = styled(Heading)`
  font-weight: 400;
`

const FEATURES = [
  {
    imageId: 'lebt-landing-feature-1',
    smallImageId: 'lebt-landing-feature-1-small',
    title: 'All-in-one booking platform',
    description: 'Book flights, hotels and car hire, all in one place.',
  },
  {
    imageId: 'lebt-landing-feature-2-v2',
    smallImageId: 'lebt-landing-feature-2-small',
    title: 'Customisable policies',
    description: 'Set rules once and let approvals and compliance happen automatically.',
  },
  {
    imageId: 'lebt-landing-feature-3',
    smallImageId: 'lebt-landing-feature-3-small',
    title: 'Real-time insights & reporting',
    description: 'See where every dollar goes, as it happens. No spreadsheets required.',
  },
  {
    imageId: 'lebt-landing-feature-4-v2',
    smallImageId: 'lebt-landing-feature-4-small',
    title: 'Customer support',
    description: 'Dedicated travel specialists available whenever you or your travellers need help.',
  },
]

function BusinessTravellerFeaturesSection() {
  const showModal = useContext(ModalContext)
  const openDemoModal = useCallback(() => {
    showModal(<BusinessTravellerRequestDemoModal />)
  }, [showModal])

  return <SectionBackground>
    <SectionContainer>
        <Group direction="vertical" gap={40} tabletGap={48} tabletHorizontalAlign="center">
          <SectionHeading variant="heading2" align="start" tabletAlign="center">
            <i>Simplify</i> bookings, approvals and spend
          </SectionHeading>

          <FeaturesGrid>
            {FEATURES.map(feature => (
              <FeatureCard key={feature.title}>
                <picture>
                  <source media={`(min-width: ${breakpointSizes.tablet.min}px)`} srcSet={getImageUrl(feature.imageId)} />
                  <FeatureImage
                    src={getImageUrl(feature.smallImageId)}
                    alt={feature.title}
                  />
                </picture>
                <FeatureTextGroup direction="vertical" gap={4} tabletGap={8}>
                  <Heading variant="heading4">{feature.title}</Heading>
                  <BodyText variant="large">{feature.description}</BodyText>
                </FeatureTextGroup>
              </FeatureCard>
            ))}
          </FeaturesGrid>

          <Group direction="vertical" gap={16} tabletDirection="horizontal" tabletHorizontalAlign="center">
            <TextButton kind="primary" size="medium" fit="mobile-full-width" to={BUSINESS_TRAVELLER_BUSINESS_SIGNUP_ROUTE}>
              Get started for free
            </TextButton>
            <OutlineTextButton kind="secondary" size="medium" onClick={openDemoModal} fit="mobile-full-width">
              Book a free demo
            </OutlineTextButton>
          </Group>
        </Group>
    </SectionContainer>
  </SectionBackground>
}

export default BusinessTravellerFeaturesSection
